import {
  Button,
  Card,
  DuplicateIcon,
  ImportIcon,
  LazyLoader,
  Modal,
  SearchInput,
  Skeleton,
  TabBar,
  Tooltip,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { TimeGridEntry } from '../../../../../components/AvailabilityMatrix/AvailabilityMatrix';
import { ConditionsControlProps } from '../../../../../components/Constraints/types';
import { useTimetableStore } from '../../../../../components/TimetableGrid/TimetableProvider';
import { TimetableVersionTableType } from '../../../../../components/TimetableVersion/graphql/types';
import { DuplicateAvailabilityModal } from '../../../../../components/TimetableVersion/TimetableVersionDataAvailabilityMatrix/DuplicateAvailabilityModal';
import {
  expandAvailabilityMatrixWithPauses,
  initialMatrix,
} from '../../../../../components/TimetableVersion/utils/timetableVersionTableUtils';
import { VERSION_RESOURCE_TYPE } from '../../../../../components/TimetableVersion/utils/versionResourceType';
import { useIsMobile } from '../../../../../hooks/useIsMobile';
import { useLoadBasicData } from '../../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../../hooks/useMemorizedCacheTag';
import type { WeekAvailability } from '../../../../../types/availability';
import {
  useAddTimetableVersionResourcesMutation,
  useRemoveTimetableVersionResourcesMutation,
} from '../../../../../types/planung-graphql-client-defs';
import { createLessonFilterUrl } from '../../../../../utils/create-lesson-filter-url';
import { showSuccessUpdateToast, showUserErrorToast } from '../../../../../utils/toast';
import { availabilityStore } from '../TimetableVersion';
import styles from './TimetableVersionData.module.scss';

type TimetableStore = ReturnType<typeof useTimetableStore>;
type BasicData = ReturnType<typeof useLoadBasicData>;

type MatrixRenderArgs = {
  selected: TimetableVersionTableType | null;
  loading: boolean;
  versionUuid?: string;
  timeGridEntries: TimeGridEntry[];
  onChange: (items: WeekAvailability) => void;
};

type TableRenderArgs = {
  versionUuid: string;
  versionUuids: string[];
  search: string;
  onRowSelection: (selected: TimetableVersionTableType) => void;
  onRemove: (uuids: string[]) => void;
  onShowLessonClick: (uuid: string) => void;
  /** Oeffnet den Import der Ressource - fuer den Button im Leerzustand der Tabelle. */
  onImportClick: () => void;
};

/**
 * Shared shell for the per-resource timetable-version data pages (Classes/Teachers/Subjects/Rooms).
 * These pages were near-identical copies that had drifted apart (Rooms e.g. skipped the version filter
 * and the readonly guard); this shell unifies them so they behave consistently. Per-resource variation
 * lives in the config below — including the optional `renderImport` slot Classes uses to keep its
 * dedicated import modal, and the `renderConditions` slot each resource uses for its conditions tab.
 */
export type TimetableResourceConfig = {
  availabilityResourceType: 'class' | 'teacher' | 'subject' | 'room';
  /** Relation field on the timetable-version update input. */
  relationKey: 'classes' | 'teachers' | 'subjects' | 'rooms';
  lessonFilterColumn: Parameters<typeof createLessonFilterUrl>[0]['column'];
  importLabelKey: string;
  selectVersionUuids: (store: TimetableStore) => string[];
  selectEntities: (basicData: BasicData) => readonly { uuid: string }[] | undefined;
  mapRow: (entity: { uuid: string }) => TimetableVersionTableType;
  addEntities: (store: TimetableStore, entities: readonly { uuid: string }[]) => void;
  removeEntities: (store: TimetableStore, uuids: string[]) => void;
  renderMatrix: (args: MatrixRenderArgs) => ReactNode;
  renderTable: (args: TableRenderArgs) => ReactNode;
  /** Optional per-resource conditions tab content; falls back to a placeholder when absent. */
  renderConditions?: (
    args: { versionUuid?: string; selected: TimetableVersionTableType | null } & ConditionsControlProps,
  ) => ReactNode;
  /** Optional custom import UI (button + modal); replaces the default direct-import button.
   *  Der Oeffnungszustand liegt in der Shell, damit auch der Leerzustand der Tabelle ihn oeffnen kann. */
  renderImport?: (args: { versionUuid?: string; isOpen: boolean; onOpenChange: (open: boolean) => void }) => ReactNode;
};

/** Builds a config with the concrete resource entity type checked, then erases it for the shell. */
export function timetableResourceConfig<E extends { uuid: string }>(config: {
  availabilityResourceType: 'class' | 'teacher' | 'subject' | 'room';
  relationKey: 'classes' | 'teachers' | 'subjects' | 'rooms';
  lessonFilterColumn: Parameters<typeof createLessonFilterUrl>[0]['column'];
  importLabelKey: string;
  selectVersionUuids: (store: TimetableStore) => string[];
  selectEntities: (basicData: BasicData) => readonly E[] | undefined;
  mapRow: (entity: E) => TimetableVersionTableType;
  addEntities: (store: TimetableStore, entities: readonly E[]) => void;
  removeEntities: (store: TimetableStore, uuids: string[]) => void;
  renderMatrix: (args: MatrixRenderArgs) => ReactNode;
  renderTable: (args: TableRenderArgs) => ReactNode;
  renderConditions?: (
    args: { versionUuid?: string; selected: TimetableVersionTableType | null } & ConditionsControlProps,
  ) => ReactNode;
  /** Optional custom import UI (button + modal); replaces the default direct-import button.
   *  Der Oeffnungszustand liegt in der Shell, damit auch der Leerzustand der Tabelle ihn oeffnen kann. */
  renderImport?: (args: { versionUuid?: string; isOpen: boolean; onOpenChange: (open: boolean) => void }) => ReactNode;
}): TimetableResourceConfig {
  return config as unknown as TimetableResourceConfig;
}

export const TimetableResourceDataPage = observer(({ config }: { config: TimetableResourceConfig }) => {
  const { t } = useTranslation();
  const { versionUuid } = useParams();
  const navigate = useNavigate();

  const store = useTimetableStore();
  const timeAvailabilityStore = store.getTimeAvailabilityStore();

  const [tab, setTab] = useState<'availability' | 'condition'>('availability');
  const [selected, setSelected] = useState<TimetableVersionTableType | null>(null);
  // mobil: Details (Verfügbarkeiten/Bedingungen) des angetippten Eintrags im Modal statt rechter Spalte
  const isMobile = useIsMobile();
  const [detailOpen, setDetailOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  // Conditions-tab action-bar state lives here so the add/edit + duplicate buttons sit next to the
  // availability-duplicate button; the conditions component renders the matching modals.
  const [conditionsEditOpen, setConditionsEditOpen] = useState(false);
  const [conditionsDuplicateOpen, setConditionsDuplicateOpen] = useState(false);
  /** Nur fuer Ressourcen mit eigenem Import-Modal (Klassen); siehe `renderImport`. */
  const [importOpen, setImportOpen] = useState(false);

  const versionsContext = useMemorizedCacheTag('TIMETABLE_VERSIONS');
  const [, addResources] = useAddTimetableVersionResourcesMutation();
  const [, removeResources] = useRemoveTimetableVersionResourcesMutation();
  const resourceType = VERSION_RESOURCE_TYPE[config.availabilityResourceType];

  const basicData = useLoadBasicData({ pause: !versionUuid });
  const entities = config.selectEntities(basicData);
  const versionUuids = config.selectVersionUuids(store);
  const versionKey = versionUuids.join('|');

  // Keyed by the stable entity list + the version-uuid set (versionUuids is a fresh array each render).
  // biome-ignore lint/correctness/useExhaustiveDependencies: versionUuids is captured via versionKey
  const rows = useMemo<TimetableVersionTableType[]>(
    () => entities?.filter((e) => versionUuids.includes(e.uuid)).map(config.mapRow) ?? [],
    [config, entities, versionKey],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: versionUuids is captured via versionKey
  const delta = useMemo(() => entities?.filter((e) => !versionUuids.includes(e.uuid)) ?? [], [entities, versionKey]);

  useEffect(() => {
    setSelected(rows[0] ?? null);
  }, [rows]);

  // Close any open conditions modal when the selected resource changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: selected?.uuid is a trigger-only dependency
  useEffect(() => {
    setConditionsEditOpen(false);
    setConditionsDuplicateOpen(false);
  }, [selected?.uuid]);

  const duplicateTargets = rows
    .filter((row) => row.uuid !== selected?.uuid)
    .map((row) => ({ value: row.uuid, label: row.name }));

  async function handleChange(items: WeekAvailability) {
    if (store.readonly) return;
    if (versionUuid && selected) {
      store.removeConflicts();
      setLoading(true);
      // The matrix hides pauses, but they still occupy an `order`; expand the compact matrix so pause
      // slots get a bit before persisting and feeding the conflict store (both index by `order`).
      const expanded = expandAvailabilityMatrixWithPauses(items, store.timeGridEntries ?? []);
      await availabilityStore?.setAvailability(config.availabilityResourceType, selected.uuid, expanded, versionUuid);
      timeAvailabilityStore?.setResourceAvailability(
        [
          {
            uuid: selected.uuid,
            days: {
              mon: expanded.mon,
              tue: expanded.tue,
              wed: expanded.wed,
              thu: expanded.thu,
              fri: expanded.fri,
              sat: expanded.sat,
              sun: expanded.sun,
            },
          },
        ],
        config.availabilityResourceType,
      );
      store.setConflicts();
      setLoading(false);
    }
  }

  async function handleDuplicate(targetUuids: string[]) {
    if (store.readonly || !versionUuid || !selected) return;
    setLoading(true);
    store.removeConflicts();
    const source = availabilityStore?.getAvailability(config.availabilityResourceType, selected.uuid) ?? initialMatrix;
    // Pauses are hidden in the matrix but occupy an `order`; expand so their slots get a bit before
    // persisting and feeding the conflict store (both index by `order`).
    const expanded = expandAvailabilityMatrixWithPauses(source, store.timeGridEntries ?? []);
    for (const targetUuid of targetUuids) {
      await availabilityStore?.setAvailability(config.availabilityResourceType, targetUuid, expanded, versionUuid);
      timeAvailabilityStore?.setResourceAvailability(
        [
          {
            uuid: targetUuid,
            days: {
              mon: expanded.mon,
              tue: expanded.tue,
              wed: expanded.wed,
              thu: expanded.thu,
              fri: expanded.fri,
              sat: expanded.sat,
              sun: expanded.sun,
            },
          },
        ],
        config.availabilityResourceType,
      );
    }
    store.setConflicts();
    setLoading(false);
    setDuplicateOpen(false);
    showSuccessUpdateToast();
  }

  async function handleImport() {
    if (store.readonly) return;
    setLoading(true);
    if (versionUuid && delta.length > 0) {
      // Neue Ressourcen bekommen im Backend die offene Verfügbarkeit.
      const result = await addResources(
        { versionUuid, resourceType, uuids: delta.map((d) => d.uuid) },
        versionsContext,
      );
      if (!result || result.error) {
        showUserErrorToast({ text: t('common.errorToastText'), error: result.error });
      } else {
        showSuccessUpdateToast();
      }
    }
    config.addEntities(store, delta);
    setLoading(false);
  }

  const onRemove = async (uuids: string[]) => {
    if (store.readonly) return;
    setSelected(rows[0] ?? null);
    if (!versionUuid || uuids.length === 0) return;
    await removeResources({ versionUuid, resourceType, uuids }, versionsContext);
    config.removeEntities(store, uuids);
  };

  const onShowLessonClick = (uuid: string) => {
    if (versionUuid) {
      navigate(
        createLessonFilterUrl({
          context: 'stundenplan',
          versionUuid: versionUuid,
          column: config.lessonFilterColumn,
          filterValue: uuid,
        }),
      );
    }
  };

  const detailCard = (
    <Card className={styles['version-data']} contentPadding='none' fitContent>
      <small>{t('timetableVersion.specificInfo', { for: selected?.name })}</small>
      <div className={styles['mini-tab']}>
        <TabBar
          backgroundColor={'none'}
          onValueChange={(content) => setTab(content as 'availability' | 'condition')}
          tabs={[
            {
              trigger: t('availability.title', { count: 2 }),
              id: 'availability',
              content: config.renderMatrix({
                selected,
                loading,
                versionUuid,
                timeGridEntries: store.timeGridEntries ?? [],
                onChange: handleChange,
              }),
            },
            {
              trigger: t('conditions.title', { count: 2 }),
              id: 'condition',
              content: config.renderConditions ? (
                <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
                  {config.renderConditions({
                    versionUuid,
                    selected,
                    editOpen: conditionsEditOpen,
                    setEditOpen: setConditionsEditOpen,
                    duplicateOpen: conditionsDuplicateOpen,
                    setDuplicateOpen: setConditionsDuplicateOpen,
                    duplicateTargets,
                  })}
                </Suspense>
              ) : (
                <div className='px-6 py-4'>
                  <Skeleton />
                </div>
              ),
            },
          ]}
        />
        <div className={styles['additional-actions']}>
          {tab === 'availability' && (
            <Tooltip content={t('availability.duplicate')}>
              <Button
                hierarchy='ghost'
                icon={<DuplicateIcon />}
                onClick={() => setDuplicateOpen(true)}
                disabled={store.readonly || !selected || rows.length < 2}
              />
            </Tooltip>
          )}
          {tab === 'condition' && config.renderConditions && (
            <Tooltip content={t('constraint.duplicate')}>
              <Button
                hierarchy='ghost'
                icon={<DuplicateIcon />}
                onClick={() => setConditionsDuplicateOpen(true)}
                disabled={store.readonly || !selected || rows.length < 2}
              />
            </Tooltip>
          )}
        </div>
        <DuplicateAvailabilityModal
          isOpen={duplicateOpen}
          onClose={() => setDuplicateOpen(false)}
          sourceName={selected?.name ?? ''}
          options={rows
            .filter((entry) => entry.uuid !== selected?.uuid)
            .map((entry) => ({ value: entry.uuid, label: entry.name }))}
          isLoading={loading}
          onConfirm={handleDuplicate}
        />
      </div>
    </Card>
  );

  return (
    <div>
      <div className={'tks__grid'}>
        <div
          className={`tks__row ${styles.toolbar}`}
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)' }}
        >
          <SearchInput
            name={`${config.relationKey}-search`}
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={{ marginLeft: 'auto' }}>
            {config.renderImport ? (
              config.renderImport({ versionUuid, isOpen: importOpen, onOpenChange: setImportOpen })
            ) : (
              <Button
                hierarchy='tertiary'
                icon={<ImportIcon />}
                onClick={handleImport}
                disabled={!delta.length || loading || store.readonly}
              >
                {t('timetableVersion.import', { import: t(config.importLabelKey, { count: 2 }) })}
              </Button>
            )}
          </div>
        </div>
        <div className={'tks__row'}>
          <div className={`${rows.length === 0 || isMobile ? 'col-xs-12' : 'col-xs-6'} ${styles['left']}`}>
            {config.renderTable({
              versionUuid: versionUuid ?? '',
              versionUuids,
              search,
              onRowSelection: (s) => {
                setSelected(s);
                if (isMobile) setDetailOpen(true);
              },
              onRemove,
              onShowLessonClick,
              onImportClick: config.renderImport ? () => setImportOpen(true) : handleImport,
            })}
          </div>
          {rows.length !== 0 && !isMobile && (
            <div className={'tks__col col-xs-6'}>
              <Suspense fallback={<LazyLoader />}>{detailCard}</Suspense>
            </div>
          )}
        </div>
      </div>

      {isMobile && selected && (
        <Modal title={selected.name} isOpen={detailOpen} onRequestClose={() => setDetailOpen(false)}>
          <Suspense fallback={<LazyLoader />}>{detailCard}</Suspense>
        </Modal>
      )}
    </div>
  );
});
